import { VideoEditorLogger } from '../../utility/VideoEditorLogger';
import { TranscriptTrack } from '../models/TranscriptTrack';
import { IdGenerator } from '../services/IdGenerator';
import { EditorChangeType } from '../types';
import { AbstractCommand } from './AbstractCommand';
import { CreateScenesByMediaTimeCommand } from './CreateScenesByMediaTimeCommand';
import {
  snapRangesToSceneBoundaries,
  type TimeRange,
} from './lib/snapRangesToSceneBoundaries';
import { buildReelPortion } from './reel/reelPortion';
import { RestoreTranscriptFromOriginalCommand } from './transcript/RestoreTranscriptFromOriginalCommand';

import type { VideoTranscriptElement } from '../models/elements/VideoTranscriptElement';

import type { NaturalDimensions } from '../types/aspectRatio';
import type { SceneDetection } from '../types/sceneDetection';
import type { VideoEditor } from '../VideoEditor';

const OVERLAP_EPSILON = 1e-4;

/**
 * CropTimelineToReelCommand
 *
 * Reel-specific alternative to {@link CropTimelineCommand}. Instead of trimming
 * transcript elements in place (and preserving their clip transcript segments),
 * it rebuilds the timeline from scratch for a reel:
 *
 * 1. Snap each range's start/end to nearby scene boundaries (within threshold).
 * 2. Crop the video-transcript track to the kept ranges using plain-video trim
 *    math (no per-segment splitting), laying the kept portions out contiguously
 *    in the order the ranges were passed. The elements keep their (now stale)
 *    clip transcript segments — those are discarded in the next step.
 * 3. Rebuild the transcript track from the original transcript
 *    ({@link RestoreTranscriptFromOriginalCommand}), which recomputes each
 *    element's segment from its media time.
 * 4. Rebuild the scenes track from media-time scene detections
 *    ({@link CreateScenesByMediaTimeCommand}).
 *
 * This command only supports timelines whose sole track is the video-transcript
 * track — it does not crop elements on any other track, so any additional track
 * (b-roll, audio, etc.) would be left misaligned. When that precondition is not
 * met it makes no changes and returns false.
 *
 * Undo restores the pre-command timeline snapshot.
 */
export class CropTimelineToReelCommand extends AbstractCommand {
  private keepRanges: TimeRange[];
  private sceneDetections: SceneDetection[];
  private naturalMediaDimensions: Record<string, NaturalDimensions> | undefined;
  private sceneSnapThreshold: number;

  private executed = false;
  private originalTranscriptTrackId: string | undefined;
  private originalTimelineDuration: number | null = null;

  constructor(
    editor: VideoEditor,
    keepRanges: TimeRange[],
    sceneDetections: SceneDetection[] = [],
    naturalMediaDimensions?: Record<string, NaturalDimensions>,
    sceneSnapThresholdFrames: number = 0,
  ) {
    super(editor);
    this.keepRanges = keepRanges;
    this.sceneDetections = sceneDetections;
    this.naturalMediaDimensions = naturalMediaDimensions;
    this.sceneSnapThreshold = Math.max(0, sceneSnapThresholdFrames);
  }

  getName(): string {
    return 'CropTimelineToReelCommand';
  }

  getDescription(): string {
    return `Crop timeline to reel of ${this.keepRanges.length} range(s)`;
  }

  /**
   * Only proceed when the single track on the timeline is the video-transcript
   * track. Other tracks are not cropped and would be left misaligned.
   */
  private getSoleTranscriptTrack(): TranscriptTrack | null {
    const tracks = this.editor.timeline.tracks;
    if (tracks.length !== 1) {
      return null;
    }
    const [track] = tracks;
    return track instanceof TranscriptTrack ? track : null;
  }

  execute(): boolean {
    if (this.executed) return false;

    super.execute(); // captures a timeline snapshot for undo

    try {
      VideoEditorLogger.logCommand(this, 'start', {
        keepRanges: this.keepRanges,
      });

      const track = this.getSoleTranscriptTrack();
      if (!track) {
        VideoEditorLogger.logCommand(this, 'error', {
          error: 'not_single_video_transcript_track',
          trackCount: this.editor.timeline.tracks.length,
        });
        return false;
      }

      if (!this.editor.getOriginalTranscript()) {
        VideoEditorLogger.logCommand(this, 'error', {
          error: 'no_original_transcript',
        });
        return false;
      }

      this.originalTranscriptTrackId = this.editor.getTranscriptTrack()?.id;
      this.originalTimelineDuration = this.editor.timeline.duration ?? null;

      const ranges = this.resolveRanges();
      const totalDuration = this.cropTrackToPlainVideoElements(track, ranges);
      if (totalDuration <= 0) {
        VideoEditorLogger.logCommand(this, 'error', {
          error: 'no_elements_kept',
          keepRanges: ranges,
        });
        return false;
      }

      this.editor.timeline.setDuration(totalDuration);

      // Rebuild the transcript track from the original transcript so the plain
      // video elements regain clip transcript segments aligned to their media.
      const restored = new RestoreTranscriptFromOriginalCommand(
        this.editor,
      ).execute();
      if (!restored) {
        VideoEditorLogger.logCommand(this, 'error', {
          error: 'restore_transcript_failed',
        });
        return false;
      }

      // Rebuild scenes from media-time detections against the new timeline.
      if (this.sceneDetections.length > 0) {
        new CreateScenesByMediaTimeCommand(
          this.editor,
          this.sceneDetections,
          this.naturalMediaDimensions,
        ).execute();
      }

      this.editor.markDirty('crop_timeline_to_reel_command');
      this.editor.notifyObservers(EditorChangeType.TIMELINE_CHANGED, {
        type: 'timeline_cropped_to_reel',
        keepRanges: ranges,
      });

      this.executed = true;
      VideoEditorLogger.logCommand(this, 'complete', { keepRanges: ranges });
      return true;
    } catch (error) {
      VideoEditorLogger.logCommand(this, 'error', {
        error: error instanceof Error ? error.message : String(error),
      });
      return false;
    }
  }

  /**
   * Frame-snaps each range then snaps to nearby scene boundaries. Range order
   * is preserved and ranges are never sorted or merged — the reel plays clips
   * in the order they were requested.
   */
  private resolveRanges(): TimeRange[] {
    const frameSnapped = this.keepRanges.map(range => ({
      start: this.editor.timeline.snapTimeToFrame(range.start),
      end: this.editor.timeline.snapTimeToFrame(range.end),
    }));
    return snapRangesToSceneBoundaries(
      this.editor.timeline,
      frameSnapped,
      this.sceneSnapThreshold,
    );
  }

  /**
   * Replaces the video-transcript track with one whose elements are the kept
   * portions of each range, laid out contiguously from time 0 in range order.
   *
   * Each portion is a clone of its source element trimmed with plain-video
   * math — only time/trim are adjusted, the clip transcript segment is left
   * untouched (and stale). {@link RestoreTranscriptFromOriginalCommand}
   * recomputes those segments from media time afterwards. Returns the total
   * reel duration.
   *
   * PLAT-11050 — each range is one reel Section, so every portion built for a
   * range is stamped with that range's freshly minted `reelSectionId`. The
   * stamp rides in element state, so the transcript re-projection in step 3
   * carries it forward rather than erasing it.
   */
  private cropTrackToPlainVideoElements(
    track: TranscriptTrack,
    ranges: TimeRange[],
  ): number {
    // Read via the overridden `elements` getter (base `sortedElements` reflects
    // a different internal array on TranscriptTrack).
    const sourceElements = [...track.elements].sort((a, b) => a.time - b.time);
    const newElements: VideoTranscriptElement[] = [];
    let offset = 0;

    for (const range of ranges) {
      // One Section per range (DE5/DE15 — a plain clip is a reel of one).
      const reelSectionId = IdGenerator.shortRandomId();

      for (const el of sourceElements) {
        const portionStart = Math.max(el.time, range.start);
        const portionEnd = Math.min(el.end_time, range.end);
        const portionDuration = portionEnd - portionStart;
        if (portionDuration <= OVERLAP_EPSILON) {
          continue; // no overlap with this range
        }

        const mediaStart = el.getMediaStartTime() + (portionStart - el.time);
        const portion = buildReelPortion(el, {
          time: offset,
          duration: portionDuration,
          mediaStart,
          reelSectionId,
        });

        newElements.push(portion);
        offset += portionDuration;
      }
    }

    // Swap in a fresh transcript track holding only the cropped portions.
    // Reuse the id so the editor's transcript-track reference stays valid; the
    // subsequent RestoreTranscriptFromOriginalCommand rebuilds it in full.
    const reelTrack = new TranscriptTrack({
      id: track.id,
      type: track.type,
      name: track.name,
      locked: track.locked,
      visible: track.visible,
      transcriptSegmentElements: newElements,
    });
    this.editor.timeline.replaceTrack(track.id, reelTrack);
    this.editor.setTranscriptTrack(reelTrack.id);

    return offset;
  }

  undo(): boolean {
    if (!this.executed) {
      return false;
    }

    const result = super.undo();

    // The transcript restore re-pointed transcriptTrackId at the rebuilt track;
    // the snapshot restores the tracks themselves but not that id.
    if (result && this.originalTranscriptTrackId) {
      this.editor.setTranscriptTrack(this.originalTranscriptTrackId);
    }

    if (result && this.originalTimelineDuration !== null) {
      this.editor.timeline.setDuration(this.originalTimelineDuration);
    }

    VideoEditorLogger.logCommand(this, 'complete', {
      operation: 'undo',
      result,
    });

    return result;
  }
}
